import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { apiScrabble } from '../utils/api';
import { DICTIONARIES, loadDict, saveDict } from '../utils/dicts';
import DictInfo from '../components/DictInfo';
import DefinableWord from '../components/DefinableWord';
import SeoContent from '../components/SeoContent';
import PageSEO from '../components/PageSEO';

const SCORES = {a:1,e:1,i:1,o:1,u:1,l:1,n:1,r:1,s:1,t:1,d:2,g:2,b:3,c:3,m:3,p:3,f:4,h:4,v:4,w:4,y:4,k:5,j:8,x:8,q:10,z:10};

function TileDisplay({ letters }) {
  return (
    <span>
      {letters.toUpperCase().split('').map((ch, i) => (
        <span key={i} className="scrabble-tile">{ch}<sup className="scrabble-score">{SCORES[ch.toLowerCase()]||0}</sup></span>
      ))}
    </span>
  );
}

export default function ScrabbleFinder() {
  const [tiles, setTiles]   = useState('');
  const [dict, setDict]     = useState(loadDict);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');
  const [minScore, setMinScore] = useState(0);

  async function solve(dictKey) {
    if (!tiles.trim()) return;
    setLoading(true); setError(''); setResults(null);
    try {
      const { data } = await apiScrabble(tiles, dictKey);
      setResults(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong.');
    } finally { setLoading(false); }
  }

  function handleSolve(e) {
    e.preventDefault();
    solve(dict);
  }

  function handleDictChange(e) {
    const next = e.target.value;
    setDict(next);
    saveDict(next);
    if (results !== null) solve(next);   // re-run with the new dictionary
  }

  const filtered = results?.results?.filter(r => r.score >= minScore) || [];

  return (
    <div className="game-page">
      <PageSEO page="scrabble" />
      <div className="container" style={{maxWidth:750}}>
        <Link to="/" className="back-btn"><i className="bi bi-arrow-left"></i> All Games</Link>

        <div className="game-header">
          <span className="game-icon">🃏</span>
          <h1>Scrabble Word Finder</h1>
          <p>Enter your Scrabble tiles and find the highest-scoring words you can play!</p>
        </div>

        <div className="solver-card">
          <div className="p-3 mb-3 rounded-3" style={{background:'#fef3c7',border:'1px solid #fbbf24'}}>
            <p className="mb-1 fw-bold" style={{color:'#92400e'}}>Tile Rules:</p>
            <ul className="mb-0 small fw-bold" style={{color:'#b45309'}}>
              <li>Enter your 7 (or fewer) tiles as letters</li>
              <li>Use <strong>?</strong> for blank/wildcard tiles</li>
              <li>Example: <code>AEILNRT</code> or <code>AEILR?T</code></li>
            </ul>
          </div>

          <form onSubmit={handleSolve}>
            <div className="mb-3">
              <label className="fw-bold mb-1">Your Tiles</label>
              <input
                className="big-input"
                value={tiles}
                onChange={e => setTiles(e.target.value.replace(/[^a-zA-Z?]/g,'').toUpperCase())}
                placeholder="e.g. AEILNRT"
                maxLength={10}
                autoFocus
              />
              {tiles && <div className="mt-2"><TileDisplay letters={tiles.replace(/\?/g,'')} /></div>}
            </div>
            <div className="mb-3">
              <div className="d-flex align-items-center gap-2 mb-1">
                <label className="fw-bold mb-0">Dictionary</label>
                <DictInfo />
              </div>
              <select
                className="form-select"
                value={dict}
                onChange={handleDictChange}
                style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 600, borderRadius: 10, maxWidth: 360 }}
              >
                {DICTIONARIES.map(d => (
                  <option key={d.key} value={d.key}>{d.label}</option>
                ))}
              </select>
            </div>
            <button type="submit" className="solve-btn" disabled={loading || !tiles}>
              {loading ? <><span className="spinner-border spinner-border-sm me-2"></span>Finding…</> : '🃏 Find Words'}
            </button>
          </form>
        </div>

        {error && <div className="alert alert-danger alert-custom">{error}</div>}

        {results !== null && (
          <div className="solver-card">
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <p className="result-count mb-0">
                Found <strong>{filtered.length}</strong> playable word{filtered.length !== 1 ? 's' : ''}
                <span className="text-muted fw-normal d-block" style={{ fontSize: '0.8rem' }}>💡 Click a word for its meaning</span>
              </p>
              <div className="d-flex align-items-center gap-2">
                <small className="fw-bold text-muted">Min score:</small>
                {[0,5,10,15,20].map(s => (
                  <button key={s} type="button"
                    className="btn btn-sm fw-bold"
                    style={{
                      borderRadius:'50px', width:'36px', height:'36px', padding:0,
                      background: minScore === s ? 'var(--warning)' : 'var(--border)',
                      color: minScore === s ? '#fff' : 'var(--text)', border:'none',
                      fontFamily:'Nunito,sans-serif'
                    }}
                    onClick={() => setMinScore(s)}
                  >{s}</button>
                ))}
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-hover" style={{fontFamily:'Nunito,sans-serif'}}>
                <thead>
                  <tr>
                    <th className="fw-bold">Word</th>
                    <th className="fw-bold">Score</th>
                    <th className="fw-bold">Length</th>
                    <th className="fw-bold">Blanks Used</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.slice(0, 50).map(r => (
                    <tr key={r.word}>
                      <td>
                        <DefinableWord word={r.word} dict={dict} className="fw-bold" style={{fontSize:'1.05rem', letterSpacing:1, textTransform:'uppercase'}}>
                          {r.word}
                        </DefinableWord>
                      </td>
                      <td>
                        <span className="badge rounded-pill" style={{background:'#fef3c7',color:'#92400e',fontSize:'0.85rem',fontWeight:800}}>
                          {r.score} pts
                        </span>
                      </td>
                      <td><span className="text-muted fw-bold">{r.length}</span></td>
                      <td>
                        {r.wildcards_used > 0
                          ? <span className="badge" style={{background:'#ede9fe',color:'#5b21b6'}}>{r.wildcards_used} blank</span>
                          : <span className="text-muted">—</span>
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      <SeoContent game="scrabble" />
      </div>
    </div>
  );
}
